class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class FreeShippingProgress extends HTMLElement {
  static previousProgress;

  static confetti = [
    { x: -150, peakY: -55, fallY: 18, rotate: -240, delay: 0 },
    { x: -120, peakY: -82, fallY: 10, rotate: 190, delay: 40 },
    { x: -92, peakY: -45, fallY: 26, rotate: -170, delay: 90 },
    { x: -66, peakY: -90, fallY: 6, rotate: 260, delay: 20 },
    { x: -35, peakY: -62, fallY: 22, rotate: -210, delay: 120 },
    { x: -12, peakY: -100, fallY: 12, rotate: 280, delay: 70 },
    { x: 18, peakY: -72, fallY: 25, rotate: -190, delay: 10 },
    { x: 45, peakY: -95, fallY: 8, rotate: 230, delay: 100 },
    { x: 72, peakY: -58, fallY: 20, rotate: -260, delay: 50 },
    { x: 100, peakY: -86, fallY: 15, rotate: 200, delay: 130 },
    { x: 128, peakY: -48, fallY: 28, rotate: -220, delay: 80 },
    { x: 155, peakY: -75, fallY: 10, rotate: 250, delay: 30 },
  ];

  connectedCallback() {
    // Shopify section responses are parsed in a detached document before their
    // HTML is inserted into the live drawer. Only the live element should
    // advance animation state or schedule a celebration.
    if (this.ownerDocument !== document) return;

    const progressValue = this.querySelector('.cart-drawer__shipping-progress-value');
    const currentProgress = this.progress;
    const previousProgress = FreeShippingProgress.previousProgress;

    FreeShippingProgress.previousProgress = currentProgress;

    if (
      !progressValue ||
      this.hidden ||
      previousProgress === undefined ||
      previousProgress === currentProgress
    ) {
      return;
    }

    progressValue.style.setProperty('--free-shipping-progress', `${previousProgress}%`);
    progressValue.getBoundingClientRect();

    requestAnimationFrame(() => {
      progressValue.style.setProperty('--free-shipping-progress', `${currentProgress}%`);
    });

    if (previousProgress < 100 && currentProgress === 100) {
      window.setTimeout(() => this.celebrate(), 450);
    }
  }

  celebrate() {
    if (!this.isConnected || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const celebration = document.createElement('span');
    celebration.className = 'cart-drawer__shipping-progress-celebration';
    celebration.setAttribute('aria-hidden', 'true');

    FreeShippingProgress.confetti.forEach((settings, index) => {
      const piece = document.createElement('span');
      piece.className = 'cart-drawer__shipping-progress-confetti';
      piece.style.setProperty('--confetti-x', `${settings.x}px`);
      piece.style.setProperty('--confetti-peak-y', `${settings.peakY}px`);
      piece.style.setProperty('--confetti-fall-y', `${settings.fallY}px`);
      piece.style.setProperty('--confetti-rotate', `${settings.rotate}deg`);
      piece.style.setProperty('--confetti-delay', `${settings.delay}ms`);
      piece.style.setProperty('--confetti-color', `var(--confetti-color-${(index % 4) + 1})`);
      celebration.appendChild(piece);
    });

    this.appendChild(celebration);
    window.setTimeout(() => celebration.remove(), 1600);
  }

  get progress() {
    const progress = Number(this.dataset.progress);

    if (!Number.isFinite(progress)) return 0;
    return Math.min(Math.max(progress, 0), 100);
  }
}

customElements.define('free-shipping-progress', FreeShippingProgress);
